import classNames from "classnames";
import moment from "moment";
import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import Tooltip from "PFComponents/tooltip/tooltip";
import { Typography } from "PFComponents/typography";
import { ActionIcon } from "PFCore/components/action_icon";
import { LoadingDots } from "PFCore/components/loading_dots";
import { useMatchingActivityRefresh } from "PFCore/hooks/queries/activities/matching_activities/use_matching_activity_refresh";
import { useMatchingActivityStatus } from "PFCore/hooks/queries/activities/matching_activities/use_matching_activity_status";
import { ActivitiesCollection } from "PFCore/hooks/queries/activities/query_keys";
import { useActivitiesInvalidate } from "PFCore/hooks/queries/activities/use_activities_invalidate";
import { FetchMatchingActivityStatusParams } from "PFCore/services/matching_activities/fetch_matching_activity_status";
import { RefreshMatchingActivityResponse } from "PFCore/services/matching_activities/refresh_matching_activity";
import { Activity } from "PFTypes";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import css from "./refresh_match.module.scss";

type RefreshMatchProps = {
  activity: Pick<Activity, "id" | "post_until">;
  onSuccess: (response: RefreshMatchingActivityResponse) => void;
};

export const RefreshMatch = ({ activity, onSuccess }: RefreshMatchProps): JSX.Element | null => {
  const { t } = useTranslation("translation", { keyPrefix: "refreshMatch" });
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const growl = useGrowl();
  const { invalidateByCollection } = useActivitiesInvalidate();

  const activityStatusParams: FetchMatchingActivityStatusParams = {
    activityId: activity.id,
    profileId: currentProfile.id
  };

  const {
    mutate: refreshActivity,
    isLoading: isRefreshingActivity,
    isError
  } = useMatchingActivityRefresh(
    {
      activityId: activity.id,
      profileId: currentProfile.id
    },
    {
      onSuccess: (response) => {
        invalidateByCollection(ActivitiesCollection.MatchingActivityStatus, activityStatusParams);
        onSuccess(response);
      },
      onError: () => {
        growl({
          message: t("matchUpdateError"),
          kind: "alert"
        });
      }
    }
  );

  const { data, isFetching: isLoadingActivityStatus } = useMatchingActivityStatus(activityStatusParams);
  const needsRefresh = data?.needsRefresh;

  const hasExpired = moment().diff(activity.post_until) > 0;
  if (hasExpired) {
    return null;
  }

  if (isRefreshingActivity || isLoadingActivityStatus) {
    return <LoadingDots />;
  }

  let tooltipContent = t("updateMatch");

  if (isError) {
    tooltipContent = t("calculationDelay");
  } else if (!needsRefresh) {
    tooltipContent = t("updatedMatch");
  }

  const disabled = !needsRefresh;

  return (
    <Tooltip
      content={
        <Typography variant="bodyRegular" noMargin>
          {tooltipContent}
        </Typography>
      }
    >
      <div>
        <ActionIcon
          name="refresh"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            refreshActivity();
          }}
          disabled={disabled}
          classes={{ root: classNames({ [css.disabled]: disabled }) }}
        />
      </div>
    </Tooltip>
  );
};
